import { useMemo } from "react";
import { LibrarySendDialog as DesignSendDialog, ManuscriptLibrary } from "@studio-features/meta-crew/manuscript-library";
import { can } from "../lib/access";
import { createLibraryAdapter } from "../lib/library-adapter";
import { useCustomers } from "../lib/queries";
import { useRefineUploadProvider } from "../lib/refine-upload";
import type { Me } from "../lib/types";
import { Loading, PanelError } from "../console/ui";

/**
 * 직원 원고함 (docs/meta-crew-manuscript-library-design.md). 시안 6001c5c 부품(ManuscriptLibrary·LibrarySendDialog)에
 * 실제 API 어댑터(src/lib/library-adapter.ts)를 넘긴다. 화면·상태 처리는 시안이 맡고, 판정은 서버가 한다.
 * 관리 콘솔 전용 (고객 웹·데스크톱에는 없음).
 * 요청서 31절(시안 24ca0e6): 새 원고 창은 "완성 원고로 올리기 / 정형화해서 올리기" 두 채널. 정형화 채널은 src/lib/refine-upload.ts 의
 * provider 로 폴더 통째 올리기 → `POST /admin/manuscripts/refine/batch`, 만든 작업은 정형화 탭(`?ids=`)으로 간다.
 */

/** 고객사 목록이 바뀔 때만 어댑터를 새로 만든다 (렌더마다 새 객체면 부품이 계속 다시 읽는다). 정형화 탭도 같은 어댑터를 쓴다 */
export function useLibraryAdapterFor(enabled = true) {
  const q = useCustomers("", enabled);
  const items = q.data?.items;
  const sig = (items ?? []).map((c) => `${c.id}:${c.name}:${c.status}`).join("|");
  const adapter = useMemo(() => (items ? createLibraryAdapter(items.map((c) => ({ id: c.id, name: c.name, status: c.status }))) : null), [sig]); // eslint-disable-line react-hooks/exhaustive-deps
  return { adapter, error: q.error, retry: () => void q.refetch() };
}

export function ManuscriptLibraryScreen({
  me,
  customerId,
  onDirtyChange,
  initialDocumentId,
  onOpenRefine,
  onOpenRefineList,
  onOpenAiSettings,
}: {
  me: Me;
  customerId?: string;
  customerName?: string;
  onDirtyChange?: (dirty: boolean) => void;
  /** 정형화에서 등록한 뒤 바로 열 원고 (요청서 29절) */
  initialDocumentId?: string;
  /** 원고 상세의 "출처: 정형화 …" 링크 */
  onOpenRefine?: (id: string) => void;
  /** 정형화해서 올리기 완료 → 방금 만든 작업만 정형화 탭에서 보기 (요청서 31절) */
  onOpenRefineList?: (ids: string[]) => void;
  /** 올리기 창의 "AI 설정으로" (키 없음·실행기 연결 안 됨·예산 초과) */
  onOpenAiSettings?: () => void;
}) {
  const { adapter, error, retry } = useLibraryAdapterFor();
  const refine = useRefineUploadProvider(me, { onOpenList: onOpenRefineList, onOpenSettings: onOpenAiSettings, customerName: (id) => adapter?.customers.find((c) => c.id === id)?.name });
  if (error) return <PanelError error="고객사 목록을 불러오지 못했습니다." onRetry={retry} />;
  if (!adapter) return <Loading />;
  if (!adapter.customers.length) return <PanelError error="고객사가 없습니다." />;
  return (
    <ManuscriptLibrary
      key={customerId ?? "all"}
      provider={adapter}
      customerId={customerId}
      canWrite={can(me, "MANUSCRIPT_WRITE")}
      canSend={can(me, "TASK_CREATE")}
      onDirtyChange={onDirtyChange}
      initialDocumentId={initialDocumentId}
      // 정형화 권한이 있을 때만 "정형화해서 올리기" 채널 (없으면 완성 원고 채널만)
      refineUpload={can(me, "MANUSCRIPT_REFINE") ? refine.provider : undefined}
      onOpenRefine={onOpenRefine}
    />
  );
}

/** 작업 등록 › 원고함에서 선택 (방법 B). */
export function LibrarySendDialog({
  open,
  me,
  onClose,
  initialIds,
  initialCustomer,
  initialExecution,
  onExecutionChange,
  onDirect,
}: {
  open: boolean;
  me: Me;
  onClose: () => void;
  initialIds?: string[];
  /** 처음 열 원고 폴더의 고객사 */
  initialCustomer?: string;
  /** 작업 등록에서 이미 고른 실행 고객사 (요청서 27절: 2 실행 설정에서 다시 고르지 않게) */
  initialExecution?: string;
  onExecutionChange?: (customerId: string) => void;
  onDirect?: () => void;
}) {
  const { adapter } = useLibraryAdapterFor();
  if (!adapter || !can(me, "MANUSCRIPT_READ") || !can(me, "TASK_CREATE")) return null;
  return (
    <DesignSendDialog
      provider={adapter}
      open={open}
      initialIds={initialIds}
      initialCustomer={initialCustomer}
      initialExecution={initialExecution}
      onExecutionChange={onExecutionChange}
      onDirect={onDirect}
      onClose={onClose}
    />
  );
}
